import { FLOWS_ENUM } from '../../../common/enums';
import {
  setCurrentFlow,
  setCurrentStep,
  setOriginPath,
  setSteps,
} from './flowSlice';
import { AppThunk } from 'src/store/store';
import api from 'src/api';
import {
  ChargeableFlow,
  ExpressRegistration,
  RegistrationApplication,
  RegistrationFlow,
  RegistrationRenew,
  RegistrationUpdate,
  TaxCertificateConfig,
} from 'src/types/configuration';

export const mapApplyFlowConfigToFlowSteps = (
  completionCallback: (path?: string) => void,
  originPath = '/dashboard'
): AppThunk<void, typeof api> => {
  return async (dispatch, getState, api) => {
    const { listings, application } = getState()?.config?.settings?.express
      ?.registration as ExpressRegistration;

    const applicationSteps = [];

    // The following must occur in sequence to ensure step order
    // # 1
    if (hasRequirementsRequirement(application as RegistrationUpdate)) {
      applicationSteps.push({
        key: 'apply-requirements',
        stepNum: applicationSteps.length + 1,
        stepName: 'Application Requirements',
        isCompleted: false,
      });
    }
    // # 2
    applicationSteps.push({
      key: 'apply-property',
      stepNum: applicationSteps.length + 1,
      stepName: 'Property',
      isCompleted: false,
    });

    // # 3
    applicationSteps.push({
      key: 'apply-registrant',
      stepNum: applicationSteps.length + 1,
      stepName: 'Registrant',
      isCompleted: false,
    });

    // # 4
    applicationSteps.push({
      key: 'apply-contacts',
      stepNum: applicationSteps.length + 1,
      stepName: 'Property Contacts',
      isCompleted: false,
    });

    // # 5
    if (hasPropertyDetailsRequirement(application as RegistrationApplication)) {
      applicationSteps.push({
        key: 'apply-property-details',
        stepNum: applicationSteps.length + 1,
        stepName: 'Property Details',
        isCompleted: false,
      });
    }

    // # 6
    if (hasListingsRequirement(!!listings)) {
      applicationSteps.push({
        key: 'apply-listings',
        stepNum: applicationSteps.length + 1,
        stepName: 'Listings',
        isCompleted: false,
      });
    }

    // # 7
    if (hasDocumentsRequirement(application as RegistrationApplication)) {
      applicationSteps.push({
        key: 'apply-documents',
        stepNum: applicationSteps.length + 1,
        stepName: 'Documents',
        isCompleted: false,
      });
    }

    // # 8
    applicationSteps.push({
      key: 'apply-confirmation',
      stepNum: applicationSteps.length + 1,
      stepName: 'Confirm and Sign',
      isCompleted: false,
    });

    // # 9
    if (hasPaymentRequirement(application as RegistrationApplication)) {
      applicationSteps.push({
        key: 'apply-payment',
        stepNum: applicationSteps.length + 1,
        stepName: 'Payment',
        isCompleted: false,
      });
    }

    dispatch(setOriginPath(originPath));
    dispatch(setSteps(applicationSteps));
    dispatch(setCurrentFlow(FLOWS_ENUM.APPLY_LICENSE));
    dispatch(setCurrentStep(1));
    completionCallback(applicationSteps[0].key);
  };
};

const hasRequirementsRequirement = (application: RegistrationUpdate) => {
  const { requirements } = application;

  return requirements && requirements.length > 0;
};

const hasPropertyDetailsRequirement = (application: RegistrationFlow) => {
  const { questions } = application;
  return questions && questions.length > 0;
};

const hasDocumentsRequirement = (application: ChargeableFlow) => {
  const { documents } = application;
  return documents && documents.length > 0;
};

const hasListingsRequirement = (listings: boolean) => {
  return listings && listings === true;
};

const hasPaymentRequirement = (application: ChargeableFlow) => {
  const { fee, custom_fee, fee_override, custom_fee_multiple_answers } =
    application;

  return (
    (fee && fee !== 0) ||
    custom_fee ||
    fee_override ||
    custom_fee_multiple_answers
  );
};

const hasDocumentsRequirment = (flowConfig: RegistrationUpdate) => {
  return (
    flowConfig && flowConfig.documents && flowConfig?.documents?.length > 0
  );
};

export const setTaxSteps = (
  completionCallback: (path?: string) => void,
  skipSelectProperties = false,
  originPath = '/dashboard'
): AppThunk<void, typeof api> => {
  return async (dispatch) => {
    const taxSteps = [];

    // The following must occur in sequence to ensure step order
    // # 1
    if (!skipSelectProperties) {
      taxSteps.push({
        key: 'tax-select-properties',
        stepNum: taxSteps.length + 1,
        stepName: 'Select Properties',
        isCompleted: false,
      });
    }

    // # 2
    taxSteps.push({
      key: 'tax-report-revenue',
      stepNum: taxSteps.length + 1,
      stepName: 'Report Revenue',
      isCompleted: false,
    });

    // # 3
    taxSteps.push({
      key: 'tax-confirm-and-sign',
      stepNum: taxSteps.length + 1,
      stepName: 'Confirm and Sign',
      isCompleted: false,
    });

    // # 4
    taxSteps.push({
      key: 'tax-payment',
      stepNum: taxSteps.length + 1,
      stepName: 'Payment',
      isCompleted: false,
    });

    dispatch(setOriginPath(originPath));
    dispatch(setSteps(taxSteps));
    dispatch(setCurrentStep(1));
    dispatch(setCurrentFlow(FLOWS_ENUM.PAY_TOT));
    completionCallback(taxSteps[0].key);
  };
};

export const mapUpdateFlowConfigToFlowSteps = (
  completionCallback: (path?: string) => void,
  originPath = '/dashboard',
  isOwner: boolean
): AppThunk<void, typeof api> => {
  return async (dispatch, getState) => {
    const { update } = getState()?.config?.settings?.express
      ?.registration as ExpressRegistration;

    const updateSteps = [];

    // As per the requirements, if  the user is the owner then the registrant page should be skipped
    if (!isOwner) {
      updateSteps.push({
        key: 'update-registrant',
        stepNum: updateSteps.length + 1,
        stepName: 'Registrant Details',
        isCompleted: false,
      });
    }

    updateSteps.push({
      key: 'update-contacts',
      stepNum: updateSteps.length + 1,
      stepName: 'Property Contacts',
      isCompleted: false,
    });

    if (hasPropertyDetailsRequirement(update as RegistrationUpdate)) {
      updateSteps.push({
        key: 'update-property-details',
        stepNum: updateSteps.length + 1,
        stepName: 'Property Details',
        isCompleted: false,
      });
    }

    if (hasDocumentsRequirment(update as RegistrationUpdate)) {
      updateSteps.push({
        key: 'update-documents',
        stepNum: updateSteps.length + 1,
        stepName: 'Upload Documents',
        isCompleted: false,
      });
    }

    updateSteps.push({
      key: 'update-confirmation',
      stepNum: updateSteps.length + 1,
      stepName: 'Confirm and Sign',
      isCompleted: false,
    });

    dispatch(setOriginPath(originPath));
    dispatch(setSteps(updateSteps));
    dispatch(setCurrentFlow(FLOWS_ENUM.UPDATE_CERTIFICATE));
    dispatch(setCurrentStep(1));
    completionCallback(updateSteps[0].key);
  };
};

export const mapRenewalFlowConfigToFlowSteps = (
  completionCallback: (path?: string) => void,
  originPath = '/dashboard',
  isOwner: boolean
): AppThunk<void, typeof api> => {
  return async (dispatch, getState, api) => {
    const { renew } = getState()?.config?.settings?.express
      ?.registration as ExpressRegistration;

    const renewSteps = [];

    renewSteps.push({
      key: 'renew-requirements',
      stepNum: renewSteps.length + 1,
      stepName: 'Renewal Requirements',
      isCompleted: false,
    });

    // As per the requirements, if  the user is the owner then the registrant page should be skipped
    if (!isOwner) {
      renewSteps.push({
        key: 'renew-registrant',
        stepNum: renewSteps.length + 1,
        stepName: 'Registrant Details',
        isCompleted: false,
      });
    }

    renewSteps.push({
      key: 'renew-contacts',
      stepNum: renewSteps.length + 1,
      stepName: 'Property Contacts',
      isCompleted: false,
    });

    if (hasPropertyDetailsRequirement(renew as RegistrationRenew)) {
      renewSteps.push({
        key: 'renew-property-details',
        stepNum: renewSteps.length + 1,
        stepName: 'Property Details',
        isCompleted: false,
      });
    }

    if (hasDocumentsRequirment(renew as RegistrationRenew)) {
      renewSteps.push({
        key: 'renew-documents',
        stepNum: renewSteps.length + 1,
        stepName: 'Upload Documents',
        isCompleted: false,
      });
    }

    renewSteps.push({
      key: 'renew-confirmation',
      stepNum: renewSteps.length + 1,
      stepName: 'Confirm and Sign',
      isCompleted: false,
    });

    if (hasPaymentRequirement(renew as RegistrationRenew)) {
      renewSteps.push({
        key: 'renew-payment',
        stepNum: renewSteps.length + 1,
        stepName: 'Payment',
        isCompleted: false,
      });
    }

    dispatch(setOriginPath(originPath));
    dispatch(setSteps(renewSteps));
    dispatch(setCurrentFlow(FLOWS_ENUM.RENEW_LICENSE));
    dispatch(setCurrentStep(1));
    completionCallback(renewSteps[0].key);
  };
};

export const mapBulkRenewalFlowConfigToFlowSteps = (
  completionCallback: (path?: string) => void,
  originPath = '/dashboard'
): AppThunk<void, typeof api> => {
  return async (dispatch, getState, _api) => {
    const bulkRenewSteps = [];
    const { renew } = getState()?.config?.settings?.express
      ?.registration as ExpressRegistration;

    bulkRenewSteps.push({
      key: 'bulk-renew-select-properties',
      stepNum: bulkRenewSteps.length + 1,
      stepName: 'Select Properties',
      isCompleted: false,
    });

    bulkRenewSteps.push({
      key: 'bulk-renew-requirements',
      stepNum: bulkRenewSteps.length + 1,
      stepName: 'Renewal Requirements',
      isCompleted: false,
    });

    bulkRenewSteps.push({
      key: 'bulk-renew-confirmation',
      stepNum: bulkRenewSteps.length + 1,
      stepName: 'Confirm and Sign',
      isCompleted: false,
    });

    if (hasPaymentRequirement(renew as RegistrationRenew)) {
      bulkRenewSteps.push({
        key: 'renew-payment',
        stepNum: bulkRenewSteps.length + 1,
        stepName: 'Payment',
        isCompleted: false,
      });
    }

    dispatch(setOriginPath(originPath));
    dispatch(setSteps(bulkRenewSteps));
    dispatch(setCurrentFlow(FLOWS_ENUM.BULK_RENEWAL));
    dispatch(setCurrentStep(1));
    completionCallback(bulkRenewSteps[0].key);
  };
};

export const mapTaxCertificateFlowConfigToFlowSteps = (
  completionCallback: (path?: string) => void,
  originPath = '/dashboard'
): AppThunk<void, typeof api> => {
  return async (dispatch, getState) => {
    const { tax_certificate } = getState()?.config?.settings?.express
      ?.registration as ExpressRegistration;

    const taxCertSteps = [];

    if (hasRequirementsRequirement(tax_certificate as TaxCertificateConfig)) {
      taxCertSteps.push({
        key: 'tax-cert-requirements',
        stepNum: taxCertSteps.length + 1,
        stepName: 'Application Requirements',
        isCompleted: false,
      });
    }

    taxCertSteps.push({
      key: 'tax-cert-property',
      stepNum: taxCertSteps.length + 1,
      stepName: 'Property',
      isCompleted: false,
    });

    taxCertSteps.push({
      key: 'tax-cert-registrant',
      stepNum: taxCertSteps.length + 1,
      stepName: 'Certificate Holder',
      isCompleted: false,
    });

    taxCertSteps.push({
      key: 'tax-cert-contacts',
      stepNum: taxCertSteps.length + 1,
      stepName: 'Property Contacts',
      isCompleted: false,
    });

    taxCertSteps.push({
      key: 'tax-cert-confirmation',
      stepNum: taxCertSteps.length + 1,
      stepName: 'Confirm & Sign',
      isCompleted: false,
    });

    dispatch(setOriginPath(originPath));
    dispatch(setSteps(taxCertSteps));
    dispatch(setCurrentFlow(FLOWS_ENUM.TAX_CERTIFICATE));
    dispatch(setCurrentStep(1));
    completionCallback(taxCertSteps[0].key);
  };
};

export const setCreateAccountSteps = (
  completionCallback: (path?: string) => void,
  originPath = '/'
): AppThunk<void, typeof api> => {
  return async (dispatch, getState, api) => {
    const createAccountSteps = [];

    // The following must occur in sequence to ensure step order
    // # 1
    createAccountSteps.push({
      key: 'email-check',
      stepNum: createAccountSteps.length + 1,
      stepName: 'Check Email',
      isCompleted: false,
    });

    // # 2
    createAccountSteps.push({
      key: 'create-account',
      stepNum: createAccountSteps.length + 1,
      stepName: 'Create Account',
      isCompleted: false,
    });

    dispatch(setOriginPath(originPath));
    dispatch(setSteps(createAccountSteps));
    dispatch(setCurrentStep(1));
    completionCallback(createAccountSteps[0].key);
  };
};

export const setAdminUpdateSteps = (
  originPath = '/'
): AppThunk<void, typeof api> => {
  return async (dispatch, getState, api) => {
    const { application, renew, update } = getState()?.config?.settings?.express
      ?.registration as ExpressRegistration;

    const adminUpdateSteps = [];

    // The following must occur in sequence to ensure step order
    adminUpdateSteps.push({
      key: 'admin-update-property',
      stepNum: adminUpdateSteps.length + 1,
      stepName: 'Property',
      isCompleted: false,
    });

    adminUpdateSteps.push({
      key: 'admin-update-portal-account',
      stepNum: adminUpdateSteps.length + 1,
      stepName: 'Portal Account',
      isCompleted: false,
    });

    adminUpdateSteps.push({
      key: 'admin-update-registrant',
      stepNum: adminUpdateSteps.length + 1,
      stepName: 'Registrant',
      isCompleted: false,
    });

    adminUpdateSteps.push({
      key: 'admin-update-owner',
      stepNum: adminUpdateSteps.length + 1,
      stepName: 'Owner',
      isCompleted: false,
    });

    adminUpdateSteps.push({
      key: 'admin-update-contacts',
      stepNum: adminUpdateSteps.length + 1,
      stepName: 'Property Contacts',
      isCompleted: false,
    });

    if (
      hasPropertyDetailsRequirement(application as RegistrationApplication) ||
      hasPropertyDetailsRequirement(renew as RegistrationRenew) ||
      hasPropertyDetailsRequirement(update as RegistrationUpdate)
    ) {
      adminUpdateSteps.push({
        key: 'admin-update-property-details',
        stepNum: adminUpdateSteps.length + 1,
        stepName: 'Property Details',
        isCompleted: false,
      });
    }

    if (
      hasDocumentsRequirement(application as RegistrationApplication) ||
      hasDocumentsRequirement(renew as RegistrationRenew) ||
      hasDocumentsRequirement(update as RegistrationUpdate)
    ) {
      adminUpdateSteps.push({
        key: 'admin-update-documents',
        stepNum: adminUpdateSteps.length + 1,
        stepName: 'Documents',
        isCompleted: false,
      });
    }

    adminUpdateSteps.push({
      key: 'admin-update-confirmation',
      stepNum: adminUpdateSteps.length + 1,
      stepName: 'Confirm',
      isCompleted: false,
    });

    dispatch(setOriginPath(originPath));
    dispatch(setSteps(adminUpdateSteps));
    dispatch(setCurrentStep(1));
  };
};
